import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/Button';
import { Users, Calendar, UserCheck, LogOut, Plus, Trash2, Home, LayoutDashboard, Pencil, ArrowUp, ArrowDown, Menu, X, ArrowLeft, Globe } from 'lucide-react';
import { useStore } from '../store/usestore';

export const Admin = () => {
  const { 
    isAuthenticated, 
    login, 
    logout,
    leaders, 
    members, 
    events, 
    diasporaCoordinators,
    addLeader, 
    updateLeader,
    removeLeader, 
    addMember,
    updateMember,
    removeMember, 
    addEvent,
    updateEvent,
    removeEvent,
    addDiasporaCoordinator,
    updateDiasporaCoordinator,
    removeDiasporaCoordinator,
    fetchData,
    updateLeaderSortOrder,
    updateMemberSortOrder,
    updateEventSortOrder,
    updateDiasporaCoordinatorSortOrder,
    uploadFile,
    subscribeToRealtime,
    unsubscribeFromRealtime
  } = useStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [activeTab, setActiveTab] = useState<'dashboard' | 'leaders' | 'members' | 'events' | 'diaspora-coordinators'>('dashboard');
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ type: 'leaders' | 'members' | 'events' | 'diaspora-coordinators'; id: number } | null>(null);
  const [isClosingModal, setIsClosingModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Form States
  const [newItem, setNewItem] = useState({ name: '', title: '', role: '', date: '', image: '', images: [] as string[], bio: '', govTitle: '' });
  const [editingId, setEditingId] = useState<number | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const formRef = React.useRef<HTMLDivElement>(null);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      if (activeTab === 'events' || activeTab === 'diaspora-coordinators') {
        const newFiles = Array.from(files);
        if ((newItem.images?.length || 0) + newFiles.length > 50) {
          alert('Maximum 50 media items allowed');
          return;
        }
        setSelectedFiles(prev => [...prev, ...newFiles]);
        
        const promises = newFiles.map(file => new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(file);
        }));

        const results = await Promise.all(promises);
        setNewItem(prev => ({ ...prev, images: [...(prev.images || []), ...results] }));
      } else {
        const file = files[0];
        setSelectedFiles([file]);
        const reader = new FileReader();
        reader.onloadend = () => {
          setNewItem(prev => ({ ...prev, image: reader.result as string }));
        };
        reader.readAsDataURL(file);
      }
    }
    // Reset the input value to allow selecting the same file again in subsequent selections.
    if (e.target) {
      e.target.value = '';
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
      subscribeToRealtime();
      
      return () => {
        unsubscribeFromRealtime();
      };
    }
  }, [isAuthenticated, fetchData, subscribeToRealtime, unsubscribeFromRealtime]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await login(email, password);
    if (!success) {
      alert('Login failed. Please check your credentials.');
    }
  };

  const executeDelete = async (type: 'leaders' | 'members' | 'events' | 'diaspora-coordinators', id: number) => {
    if (type === 'leaders') await removeLeader(id);
    if (type === 'members') await removeMember(id);
    if (type === 'events') await removeEvent(id);
    if (type === 'diaspora-coordinators') await removeDiasporaCoordinator(id);
  };

  const closeModal = () => {
    setDeleteConfirmation(null);
  };

  const confirmDelete = async () => {
    if (deleteConfirmation) {
      const { type, id } = deleteConfirmation;
      closeModal();
      await executeDelete(type, id);
    }
  };

  const handleEdit = (item: any) => {
    setEditingId(item.id);
    setSelectedFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setNewItem({
      name: item.name || item.title || '',
      title: item.title || '',
      role: item.role || '',
      date: item.date || '',
      image: item.image || '',
      images: item.images || (item.image ? [item.image] : []),
      bio: item.bio || item.description || '',
      govTitle: item.govTitle || ''
    });
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const resetForm = () => {
    setNewItem({ name: '', title: '', role: '', date: '', image: '', images: [], bio: '', govTitle: '' });
    setEditingId(null);
    setSelectedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    let success = false;
    let imageUrl = newItem.image;
    let imageUrls = newItem.images || [];

    if (selectedFiles.length > 0) {
      setIsUploading(true);
      setUploadProgress(0);

      if (activeTab === 'events' || activeTab === 'diaspora-coordinators') {
        const newUploadedUrls: string[] = [];
        const totalFiles = selectedFiles.length;
        
        for (let i = 0; i < totalFiles; i++) {
          const file = selectedFiles[i];
          const isVideo = file.type.startsWith('video/') || file.type === 'video/quicktime';
          const bucket = isVideo ? 'videos' : undefined;
          const url = await uploadFile(file, bucket, (p) => {
            setUploadProgress(Math.round(((i * 100) + p) / totalFiles));
          });
          if (url) newUploadedUrls.push(url);
        }
        
        // Replace data URLs with real URLs
        let uploadIndex = 0;
        imageUrls = imageUrls.map(img => {
          if (img.startsWith('data:')) {
            return newUploadedUrls[uploadIndex++] || img;
          }
          return img;
        });
      } else {
        const file = selectedFiles[0];
        const bucket = undefined;
        const uploadedUrl = await uploadFile(file, bucket, (progress: number) => setUploadProgress(progress));
        if (uploadedUrl) {
          imageUrl = uploadedUrl;
        } else {
          setIsUploading(false);
          alert('Failed to upload image. Please try again.');
          return;
        }
      }
    }

    if (activeTab === 'leaders') {
      if (editingId) {
        success = await updateLeader(editingId, {
          name: newItem.name,
          title: newItem.title,
          govTitle: newItem.govTitle,
          image: imageUrl || '/assets/leadership-group.jpg',
          bio: newItem.bio || 'No biography available.',
          education: 'Not specified',
          achievements: []
        });
      } else {
        success = await addLeader({
          name: newItem.name,
          title: newItem.title,
          govTitle: newItem.govTitle,
          image: imageUrl || '/assets/leadership-group.jpg',
          bio: newItem.bio || 'No biography available.',
          education: 'Not specified',
          achievements: []
        });
      }
    } else if (activeTab === 'members') {
      if (editingId) {
        success = await updateMember(editingId, {
          name: newItem.name,
          role: newItem.role,
          title: newItem.title,
          image: imageUrl || `https://i.pravatar.cc/150?u=${Date.now()}`
        });
      } else {
        success = await addMember({
          name: newItem.name,
          role: newItem.role,
          title: newItem.title,
          image: imageUrl || `https://i.pravatar.cc/150?u=${Date.now()}`
        });
      }
    } else if (activeTab === 'events') {
      if (editingId) {
        success = await updateEvent(editingId, {
          title: newItem.name,
          date: newItem.date,
          location: 'TBD',
          image: imageUrls[0] || '/assets/hero-rally.jpg',
          images: imageUrls.length > 0 ? imageUrls : ['/assets/hero-rally.jpg'],
          description: ''
        });
      } else {
        success = await addEvent({
          title: newItem.name,
          date: newItem.date,
          location: 'TBD',
          image: imageUrls[0] || '/assets/hero-rally.jpg',
          images: imageUrls.length > 0 ? imageUrls : ['/assets/hero-rally.jpg'],
          description: ''
        });
      }
    } else if (activeTab === 'diaspora-coordinators') {
      if (editingId) {
        success = await updateDiasporaCoordinator(editingId, {
          image: imageUrls[0] || imageUrl || ''
        });
      } else {
        if (imageUrls.length > 0) {
          const promises = imageUrls.map(url => addDiasporaCoordinator({ image: url }));
          const results = await Promise.all(promises);
          success = results.every(r => r);
        } else {
          success = await addDiasporaCoordinator({
            image: imageUrl || ''
          });
        }
      }
    }
    
    setIsUploading(false);
    setUploadProgress(0);
    if (success) {
      resetForm();
    }
  };

  const moveItem = async (type: 'leaders' | 'members' | 'events' | 'diaspora-coordinators', id: number, direction: 'up' | 'down') => {
    let items: any[] = [];
    if (type === 'leaders') items = leaders.filter(i => !!i);
    else if (type === 'members') items = members.filter(i => !!i);
    else if (type === 'events') items = events.filter(i => !!i);
    else if (type === 'diaspora-coordinators') items = diasporaCoordinators.filter(i => !!i);

    const index = items.findIndex(item => item && item.id === id);
    if (index === -1) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;

    // Swap sort orders
    const currentItem = items[index];
    const targetItem = items[newIndex];
    const currentSortOrder = currentItem.sort_order || index + 1;
    const targetSortOrder = targetItem.sort_order || newIndex + 1;
    // Create a new array with swapped items
    const newItems = [...items];
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];

    if (type === 'leaders') {
      await updateLeaderSortOrder(currentItem.id, targetSortOrder);
      await updateLeaderSortOrder(targetItem.id, currentSortOrder);
    } else if (type === 'members') {
      await updateMemberSortOrder(currentItem.id, targetSortOrder);
      await updateMemberSortOrder(targetItem.id, currentSortOrder);
    } else if (type === 'events') {
      await updateEventSortOrder(currentItem.id, targetSortOrder);
      await updateEventSortOrder(targetItem.id, currentSortOrder);
    } else if (type === 'diaspora-coordinators') {
      await updateDiasporaCoordinatorSortOrder(currentItem.id, targetSortOrder);
      await updateDiasporaCoordinatorSortOrder(targetItem.id, currentSortOrder);
    }
    // Update sort orders for changed items
    const updates = [];
    for (let i = 0; i < newItems.length; i++) {
      const item = newItems[i];
      const newSortOrder = i + 1;
      if (item.sort_order !== newSortOrder) {
        if (type === 'leaders') updates.push(updateLeaderSortOrder(item.id, newSortOrder));
        else if (type === 'members') updates.push(updateMemberSortOrder(item.id, newSortOrder));
        else if (type === 'events') updates.push(updateEventSortOrder(item.id, newSortOrder));
        else if (type === 'diaspora-coordinators') updates.push(updateDiasporaCoordinatorSortOrder(item.id, newSortOrder));
      }
    }
    await Promise.all(updates);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="bg-white p-6 sm:p-8 rounded-xl shadow-lg w-full max-w-md">
          <div className="mb-4">
            <Link to="/" className="flex items-center text-sm text-gray-500 hover:text-primary transition-colors">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
            </Link>
          </div>
          <h2 className="text-2xl font-bold text-center mb-6 text-primary">Admin Login</h2>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                placeholder="admin@example.com"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                placeholder="Enter password"
                autoComplete="current-password"
              />
            </div>
            <Button type="submit" className="w-full">Login</Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed md:relative z-50 w-64 inset-y-0 bg-primary-dark text-white transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0
        flex flex-col
      `}>
        <div className="p-4 sm:p-6 flex justify-between items-center">
          <h2 className="text-xl font-bold">Admin Panel</h2>
          <button 
            onClick={() => setSidebarOpen(false)}
            className="md:hidden text-gray-300 hover:text-white"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        <nav className="mt-2 sm:mt-6 flex flex-col gap-1">
          <button
            onClick={() => { setActiveTab('dashboard'); setSidebarOpen(false); }}
            className={`w-full flex items-center px-4 sm:px-6 py-3 hover:bg-white/10 ${activeTab === 'dashboard' ? 'bg-white/20' : ''}`}
          >
            <LayoutDashboard className="w-5 h-5 mr-3" /> Dashboard
          </button>
          <button
            onClick={() => { setActiveTab('leaders'); setSidebarOpen(false); }}
            className={`w-full flex items-center px-4 sm:px-6 py-3 hover:bg-white/10 ${activeTab === 'leaders' ? 'bg-white/20' : ''}`}
          >
            <UserCheck className="w-5 h-5 mr-3" /> Leaders
          </button>
          <button
            onClick={() => { setActiveTab('members'); setSidebarOpen(false); }}
            className={`w-full flex items-center px-4 sm:px-6 py-3 hover:bg-white/10 ${activeTab === 'members' ? 'bg-white/20' : ''}`}
          >
            <Users className="w-5 h-5 mr-3" /> Members
          </button>
          <button
            onClick={() => { setActiveTab('events'); setSidebarOpen(false); }}
            className={`w-full flex items-center px-4 sm:px-6 py-3 hover:bg-white/10 ${activeTab === 'events' ? 'bg-white/20' : ''}`}
          >
            <Calendar className="w-5 h-5 mr-3" /> Events
          </button>
          <button
            onClick={() => { setActiveTab('diaspora-coordinators'); setSidebarOpen(false); }}
            className={`w-full flex items-center px-4 sm:px-6 py-3 hover:bg-white/10 ${activeTab === 'diaspora-coordinators' ? 'bg-white/20' : ''}`}
          >
            <Globe className="w-5 h-5 mr-3" /> Diaspora
          </button>
        </nav>
        <div className="mt-auto p-4 sm:p-6">
          <button onClick={() => logout()} className="flex items-center text-gray-300 hover:text-white">
            <LogOut className="w-5 h-5 mr-2" /> Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-3 sm:p-4 md:p-8 overflow-x-hidden">
        {/* Mobile Header */}
        <div className="flex justify-between items-center mb-4 sm:mb-8">
          <button 
            onClick={() => setSidebarOpen(true)}
            className="md:hidden p-2 text-gray-600"
          >
            <Menu className="w-6 h-6" />
          </button>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800 capitalize flex-1 truncate">
            {activeTab === 'dashboard' ? 'Dashboard Overview' : `Manage ${activeTab}`}
          </h1>
          <div className="flex items-center gap-2">
            <Link to="/" className="md:hidden">
              <Button size="sm" variant="outline" className="px-2 sm:px-3">
                <Home className="w-4 h-4" />
              </Button>
            </Link>
            <Button size="sm" onClick={() => logout()} className="hidden md:inline-flex">Logout</Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className="mb-4 sm:mb-6 flex flex-wrap gap-1 sm:gap-2 md:hidden overflow-x-auto pb-2">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`flex items-center px-2 sm:px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
              activeTab === 'dashboard' 
                ? 'bg-primary-dark text-white' 
                : 'bg-white text-gray-600 border border-gray-200'
            }`}
          >
            <LayoutDashboard className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> 
            <span className="hidden xs:inline">Dashboard</span>
          </button>
          <button
            onClick={() => setActiveTab('leaders')}
            className={`flex items-center px-2 sm:px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
              activeTab === 'leaders' 
                ? 'bg-primary-dark text-white' 
                : 'bg-white text-gray-600 border border-gray-200'
            }`}
          >
            <UserCheck className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> 
            <span className="hidden xs:inline">Leaders</span>
          </button>
          <button
            onClick={() => setActiveTab('members')}
            className={`flex items-center px-2 sm:px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
              activeTab === 'members' 
                ? 'bg-primary-dark text-white' 
                : 'bg-white text-gray-600 border border-gray-200'
            }`}
          >
            <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> 
            <span className="hidden xs:inline">Members</span>
          </button>
          <button
            onClick={() => setActiveTab('events')}
            className={`flex items-center px-2 sm:px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
              activeTab === 'events' 
                ? 'bg-primary-dark text-white' 
                : 'bg-white text-gray-600 border border-gray-200'
            }`}
          >
            <Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> 
            <span className="hidden xs:inline">Events</span>
          </button>
          <button
            onClick={() => setActiveTab('diaspora-coordinators')}
            className={`flex items-center px-2 sm:px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
              activeTab === 'diaspora-coordinators' 
                ? 'bg-primary-dark text-white' 
                : 'bg-white text-gray-600 border border-gray-200'
            }`}
          >
            <Globe className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> 
            <span className="hidden xs:inline">Diaspora</span>
          </button>
        </div>

        {activeTab === 'dashboard' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-gray-500">Total Leaders</p>
                <p className="text-2xl sm:text-3xl font-bold text-gray-900">{leaders.length}</p>
              </div>
              <UserCheck className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
            </div>
            <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-gray-500">Total Members</p>
                <p className="text-2xl sm:text-3xl font-bold text-gray-900">{members.length}</p>
              </div>
              <Users className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
            </div>
            <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-gray-500">Total Events</p>
                <p className="text-2xl sm:text-3xl font-bold text-gray-900">{events.length}</p>
              </div>
              <Calendar className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
            </div>
            <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-gray-500">Diaspora Co-ordinators</p>
                <p className="text-2xl sm:text-3xl font-bold text-gray-900">{diasporaCoordinators.length}</p>
              </div>
              <Globe className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
            </div>
          </div>
        )}

        {activeTab !== 'dashboard' && (
          <>
            {/* Add New Form */}
            <div ref={formRef} className="bg-white p-3 sm:p-6 rounded-xl shadow-sm mb-4 sm:mb-8">
              <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 flex items-center">
                {editingId ? <Pencil className="w-4 sm:w-5 h-4 sm:h-5 mr-2" /> : <Plus className="w-4 sm:w-5 h-4 sm:h-5 mr-2" />}
                {editingId ? 'Edit' : 'Add New'} {activeTab === 'diaspora-coordinators' ? 'Coordinator' : activeTab.slice(0, -1)}
              </h3>
              <div className="space-y-3">
                {activeTab !== 'diaspora-coordinators' && (
                  <input
                    type="text"
                    placeholder={activeTab === 'events' ? 'Event Title' : 'Name'}
                    value={newItem.name}
                    onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 border rounded-lg text-sm sm:text-base"
                    required
                  />
                )}
                
                {activeTab === 'leaders' && (
                  <div className="space-y-3">
                    <input
                      type="text"
                      placeholder="Title"
                      value={newItem.title}
                      onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                      className="w-full px-3 sm:px-4 py-2 border rounded-lg text-sm sm:text-base"
                      required
                    />
                    <input
                      type="text"
                      placeholder="Government Title (Optional)"
                      value={newItem.govTitle}
                      onChange={(e) => setNewItem({ ...newItem, govTitle: e.target.value })}
                      className="w-full px-3 sm:px-4 py-2 border rounded-lg text-sm sm:text-base"
                    />
                    <textarea
                      placeholder="Description / Bio"
                      value={newItem.bio}
                      onChange={(e) => setNewItem({ ...newItem, bio: e.target.value })}
                      className="w-full px-3 sm:px-4 py-2 border rounded-lg text-sm sm:text-base"
                      rows={2}
                    />
                    <div className="flex flex-col">
                      <label className="text-xs sm:text-sm text-gray-600 mb-1">Profile Picture</label>
                      {newItem.image && (
                        <div className="mb-2 relative w-20 h-20">
                          <img src={newItem.image} alt="Preview" className="w-full h-full object-cover rounded-lg border border-gray-200" />
                          <button
                            type="button"
                            onClick={() => {
                              setNewItem(prev => ({ ...prev, image: '' }));
                              if (fileInputRef.current) fileInputRef.current.value = '';
                            }}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 shadow-sm"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="w-full text-xs sm:text-sm text-gray-500 file:mr-2 sm:file:mr-4 file:py-1 sm:file:py-2 file:px-2 sm:file:px-4 file:rounded-full file:border-0 file:text-xs sm:file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary-dark border rounded-lg cursor-pointer bg-white p-1 sm:p-2"
                      />
                    </div>
                  </div>
                )}
                
                {activeTab === 'members' && (
                  <div className="space-y-3">
                    <input
                      type="text"
                      placeholder="Role"
                      value={newItem.role}
                      onChange={(e) => setNewItem({ ...newItem, role: e.target.value })}
                      className="w-full px-3 sm:px-4 py-2 border rounded-lg text-sm sm:text-base"
                      required
                    />
                    <input
                      type="text"
                      placeholder="Title (Optional)"
                      value={newItem.title}
                      onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                      className="w-full px-3 sm:px-4 py-2 border rounded-lg text-sm sm:text-base"
                    />
                    <div className="flex flex-col">
                      <label className="text-xs sm:text-sm text-gray-600 mb-1">Profile Picture</label>
                      {newItem.image && (
                        <div className="mb-2 relative w-20 h-20">
                          <img src={newItem.image} alt="Preview" className="w-full h-full object-cover rounded-lg border border-gray-200" />
                          <button
                            type="button"
                            onClick={() => {
                              setNewItem(prev => ({ ...prev, image: '' }));
                              if (fileInputRef.current) fileInputRef.current.value = '';
                            }}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 shadow-sm"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="w-full text-xs sm:text-sm text-gray-500 file:mr-2 sm:file:mr-4 file:py-1 sm:file:py-2 file:px-2 sm:file:px-4 file:rounded-full file:border-0 file:text-xs sm:file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary-dark border rounded-lg cursor-pointer bg-white p-1 sm:p-2"
                      />
                    </div>
                  </div>
                )}
                
                {activeTab === 'events' && (
                  <div className="space-y-3">
                    <div className="flex flex-col">
                      <label className="text-xs sm:text-sm text-gray-600 mb-1">Event Media (Image or Video)</label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {newItem.images?.map((img, index) => (
                          <div key={index} className="relative w-20 h-20">
                            {(img.startsWith('data:video') || img.match(/\.(mp4|webm|ogg|mov)$/i)) ? (
                              <video src={img} className="w-full h-full object-cover rounded-lg border border-gray-200" />
                            ) : (
                              <img src={img} alt="Preview" className="w-full h-full object-cover rounded-lg border border-gray-200" />
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                setNewItem(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
                                if (newItem.images.length <= 1 && fileInputRef.current) fileInputRef.current.value = '';
                              }}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 shadow-sm"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,video/*"
                        multiple
                        onChange={handleImageUpload}
                        className="w-full text-xs sm:text-sm text-gray-500 file:mr-2 sm:file:mr-4 file:py-1 sm:file:py-2 file:px-2 sm:file:px-4 file:rounded-full file:border-0 file:text-xs sm:file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary-dark border rounded-lg cursor-pointer bg-white p-1 sm:p-2"
                      />
                      {isUploading && (
                        <div className="w-full mt-4">
                          <div className="flex justify-between mb-1">
                            <span className="text-sm font-medium text-primary">Uploading...</span>
                            <span className="text-sm font-medium text-primary">{uploadProgress}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div 
                              className="bg-primary h-2.5 rounded-full transition-all duration-300" 
                              style={{ width: `${uploadProgress}%` }}
                            ></div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'diaspora-coordinators' && (
                  <div className="space-y-3">
                    <div className="flex flex-col">
                      <label className="text-xs sm:text-sm text-gray-600 mb-1">Coordinator Image(s)</label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {newItem.images?.map((img, index) => (
                          <div key={index} className="relative w-20 h-20">
                            <img src={img} alt="Preview" className="w-full h-full object-cover rounded-lg border border-gray-200" />
                            <button
                              type="button"
                              onClick={() => {
                                setNewItem(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
                                if (newItem.images.length <= 1 && fileInputRef.current) fileInputRef.current.value = '';
                              }}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 shadow-sm"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageUpload}
                        className="w-full text-xs sm:text-sm text-gray-500 file:mr-2 sm:file:mr-4 file:py-1 sm:file:py-2 file:px-2 sm:file:px-4 file:rounded-full file:border-0 file:text-xs sm:file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary-dark border rounded-lg cursor-pointer bg-white p-1 sm:p-2"
                      />
                      {isUploading && (
                        <div className="w-full mt-4">
                          <div className="flex justify-between mb-1">
                            <span className="text-sm font-medium text-primary">Uploading...</span>
                            <span className="text-sm font-medium text-primary">{uploadProgress}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div 
                              className="bg-primary h-2.5 rounded-full transition-all duration-300" 
                              style={{ width: `${uploadProgress}%` }}
                            ></div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="flex gap-2 flex-wrap">
                  <Button type="submit" size="sm" onClick={handleAdd} disabled={isUploading}>
                    {isUploading ? 'Uploading...' : (editingId ? 'Update' : 'Add Item')}
                  </Button>
                  {editingId && (
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={resetForm}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* List */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left min-w-[500px]">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-3 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm font-medium text-gray-500 uppercase">Order</th>
                      <th className="px-3 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm font-medium text-gray-500 uppercase">{activeTab === 'diaspora-coordinators' ? 'Image' : 'Name/Title'}</th>
                      <th className="px-3 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm font-medium text-gray-500 uppercase">{activeTab === 'diaspora-coordinators' ? '' : 'Position'}</th>
                      <th className="px-3 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {activeTab === 'leaders' && leaders.filter(item => !!item).map((item, index) => (
                      <tr key={item?.id || index}>
                        <td className="px-3 sm:px-6 py-2 sm:py-4">
                          <div className="flex items-center gap-1">
                            <button 
                              onClick={() => item?.id && moveItem('leaders', item.id, 'up')}
                              disabled={index === 0}
                              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                            >
                              <ArrowUp className="w-3 h-3 sm:w-4 sm:h-4" />
                            </button>
                            <button 
                              onClick={() => item?.id && moveItem('leaders', item.id, 'down')}
                              disabled={index === leaders.length - 1}
                              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                            >
                              <ArrowDown className="w-3 h-3 sm:w-4 sm:h-4" />
                            </button>
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-2 sm:py-4 font-medium text-gray-900 text-sm sm:text-base">{item?.name}</td>
                        <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-gray-500">{item?.title}</td>
                        <td className="px-3 sm:px-6 py-2 sm:py-4">
                          <div className="flex items-center gap-2">
                            <button onClick={() => item && handleEdit(item)} className="text-blue-500 hover:text-blue-700">
                              <Pencil className="w-3 h-3 sm:w-4 sm:h-4" />
                            </button>
                            <button onClick={() => item?.id && setDeleteConfirmation({ type: 'leaders', id: item.id })} className="text-red-500 hover:text-red-700">
                              <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {activeTab === 'members' && members.filter(item => !!item).map((item, index) => (
                      <tr key={item?.id || index}>
                        <td className="px-3 sm:px-6 py-2 sm:py-4">
                          <div className="flex items-center gap-1">
                            <button 
                              onClick={() => item?.id && moveItem('members', item.id, 'up')}
                              disabled={index === 0}
                              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                            >
                              <ArrowUp className="w-3 h-3 sm:w-4 sm:h-4" />
                            </button>
                            <button 
                              onClick={() => item?.id && moveItem('members', item.id, 'down')}
                              disabled={index === members.length - 1}
                              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                            >
                              <ArrowDown className="w-3 h-3 sm:w-4 sm:h-4" />
                            </button>
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-2 sm:py-4 font-medium text-gray-900 text-sm sm:text-base">{item?.name}</td>
                        <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-gray-500">{item?.role}</td>
                        <td className="px-3 sm:px-6 py-2 sm:py-4">
                          <div className="flex items-center gap-2">
                            <button onClick={() => item && handleEdit(item)} className="text-blue-500 hover:text-blue-700">
                              <Pencil className="w-3 h-3 sm:w-4 sm:h-4" />
                            </button>
                            <button onClick={() => item?.id && setDeleteConfirmation({ type: 'members', id: item.id })} className="text-red-500 hover:text-red-700">
                              <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {activeTab === 'events' && events.filter(item => !!item).map((item, index) => (
                      <tr key={item?.id || index}>
                        <td className="px-3 sm:px-6 py-2 sm:py-4">
                          <div className="flex items-center gap-1">
                            <button 
                              onClick={() => item?.id && moveItem('events', item.id, 'up')}
                              disabled={index === 0}
                              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                            >
                              <ArrowUp className="w-3 h-3 sm:w-4 sm:h-4" />
                            </button>
                            <button 
                              onClick={() => item?.id && moveItem('events', item.id, 'down')}
                              disabled={index === events.length - 1}
                              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                            >
                              <ArrowDown className="w-3 h-3 sm:w-4 sm:h-4" />
                            </button>
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-2 sm:py-4 font-medium text-gray-900 text-sm sm:text-base">{item?.title}</td>
                        <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-gray-500"></td>
                        <td className="px-3 sm:px-6 py-2 sm:py-4">
                          <div className="flex items-center gap-2">
                            <button onClick={() => item && handleEdit(item)} className="text-blue-500 hover:text-blue-700">
                              <Pencil className="w-3 h-3 sm:w-4 sm:h-4" />
                            </button>
                            <button onClick={() => item?.id && setDeleteConfirmation({ type: 'events', id: item.id })} className="text-red-500 hover:text-red-700">
                              <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {activeTab === 'diaspora-coordinators' && diasporaCoordinators.filter(item => !!item).map((item, index) => (
                      <tr key={item?.id || index}>
                        <td className="px-3 sm:px-6 py-2 sm:py-4">
                          <div className="flex items-center gap-1">
                            <button 
                              onClick={() => item?.id && moveItem('diaspora-coordinators', item.id, 'up')}
                              disabled={index === 0}
                              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                            >
                              <ArrowUp className="w-3 h-3 sm:w-4 sm:h-4" />
                            </button>
                            <button 
                              onClick={() => item?.id && moveItem('diaspora-coordinators', item.id, 'down')}
                              disabled={index === diasporaCoordinators.length - 1}
                              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                            >
                              <ArrowDown className="w-3 h-3 sm:w-4 sm:h-4" />
                            </button>
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-2 sm:py-4">
                          <img src={item?.image} alt="Coordinator" className="w-12 h-12 object-cover rounded-lg bg-gray-100" />
                        </td>
                        <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-gray-500"></td>
                        <td className="px-3 sm:px-6 py-2 sm:py-4">
                          <div className="flex items-center gap-2">
                            <button onClick={() => item && handleEdit(item)} className="text-blue-500 hover:text-blue-700">
                              <Pencil className="w-3 h-3 sm:w-4 sm:h-4" />
                            </button>
                            <button onClick={() => item?.id && setDeleteConfirmation({ type: 'diaspora-coordinators', id: item.id })} className="text-red-500 hover:text-red-700">
                              <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {((activeTab === 'leaders' && leaders.length === 0) ||
                (activeTab === 'members' && members.length === 0) ||
                (activeTab === 'events' && events.length === 0) ||
                (activeTab === 'diaspora-coordinators' && diasporaCoordinators.length === 0)) && (
                  <div className="p-4 sm:p-8 text-center text-gray-500 text-sm sm:text-base">No items found.</div>
                )}
            </div>
          </>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirmation && (
          <div className={`fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4`}>
            <div className="bg-white rounded-xl p-4 sm:p-6 max-w-sm w-full shadow-xl">
              <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2">Confirm Deletion</h3>
              <p className="text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6">
                Are you sure you want to delete this item? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-2 sm:gap-3">
                <Button variant="outline" size="sm" onClick={closeModal}>
                  Cancel
                </Button>
                <Button className="bg-red-600 hover:bg-red-700 text-white" size="sm" onClick={confirmDelete}>
                  Delete
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
